// Argus — File thumbnails (real picture/video preview lazy load).
//
// Bloc ws-direct-thumbnails 2026-05-09 — full WS direct, ZERO server cache.
//
// Design (intenție user 2026-05-09 02:15):
//   - Subscribe(item, callback) → check positive cache (data URI in-memory) → hit imediat.
//   - Check negative cache (localStorage TTL: 60s picture, 5min video). Hit → notify null.
//   - In-flight dedup: Map<fileHash, Promise> — multiple subscribers cer simultan același hash → 1 fetch real.
//   - WS request direct: wsClient.requestThumbnail(agentId, fileHash, absPath, mtime, extKind)
//       - Resolve cu pngBase64 + mimeType → cache pozitiv data URI + notify → render <img src="data:...">.
//       - Reject (TIMEOUT, AGENT_OFFLINE, EXTRACTION_FAILED): mark negative + notify null.
//   - Drop: REST GET fallback, POST /request fan-out, 1Hz HEAD poll, DB cache populate.
//     Cross-session: agent re-extract per browser session (<100ms picture SHIL_JUMBO, 5-10s video ffmpeg).

import { wsClient } from './wsClient';

const NEGATIVE_CACHE_MS_PICTURE = 60_000;
const NEGATIVE_CACHE_MS_VIDEO = 5 * 60_000;
// Key versionat — bump la fiecare schimbare semantică flow (drop REST → WS direct = .v2).
// Clean automatic la upgrade: vechiul key `argus.thumbCache.negative` rămâne în localStorage
// orfan dar nu mai e citit; clean manual via DevTools Application → Local Storage dacă deranjează.
const STORAGE_KEY = 'argus.thumbCache.negative.v2';
const POSITIVE_CACHE_MAX = 500;

export type FileThumbKind = 'picture' | 'video';

export interface FileThumbResult {
  readonly url: string;
}

interface QueueItem {
  fileHash: string;
  agentId: string;
  absPath: string;
  mtime: number;
  extKind: FileThumbKind;
}

type Subscriber = (url: string | null) => void;

class ThumbnailQueueImpl {
  // Cache pozitiv: fileHash → URL string (REST cache hit) sau data URI (WS direct extract).
  // Evict LRU la POSITIVE_CACHE_MAX entries (insertion order preserved by Map iteration spec).
  private positiveCache = new Map<string, string>();
  private negativeCache = new Map<string, number>();
  private subscribers = new Map<string, Set<Subscriber>>();
  // In-flight promise per fileHash — dedupare cross-subscribers (multiple EntryIcon visible
  // simultan = 1 fetch real, restul așteaptă același rezultat).
  private inflight = new Map<string, Promise<void>>();

  constructor() {
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored !== null) {
          const parsed = JSON.parse(stored) as Record<string, number>;
          const now = Date.now();
          for (const [hash, exp] of Object.entries(parsed)) {
            if (typeof exp === 'number' && exp > now) {
              this.negativeCache.set(hash, exp);
            }
          }
        }
      } catch { /* ignore */ }
    }
  }

  private persistNegativeCache(): void {
    if (typeof window === 'undefined') return;
    try {
      const obj: Record<string, number> = {};
      const now = Date.now();
      for (const [hash, exp] of this.negativeCache) {
        if (exp > now) obj[hash] = exp;
      }
      const entries = Object.entries(obj);
      if (entries.length > 5000) {
        entries.sort((a, b) => b[1] - a[1]);
        const trimmed: Record<string, number> = {};
        for (const [h, e] of entries.slice(0, 5000)) trimmed[h] = e;
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
      } else {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
      }
    } catch { /* ignore */ }
  }

  subscribe(item: QueueItem, callback: Subscriber): () => void {
    const cached = this.positiveCache.get(item.fileHash);
    if (cached !== undefined) {
      // Touch LRU: re-insert at end of Map (delete + set preserves recency on Map iteration).
      this.positiveCache.delete(item.fileHash);
      this.positiveCache.set(item.fileHash, cached);
      callback(cached);
      return () => {};
    }
    if (this.isNegative(item.fileHash)) {
      callback(null);
      return () => {};
    }
    let subs = this.subscribers.get(item.fileHash);
    if (subs === undefined) {
      subs = new Set();
      this.subscribers.set(item.fileHash, subs);
    }
    subs.add(callback);

    // Kick off fetch only once per fileHash (in-flight dedup).
    if (!this.inflight.has(item.fileHash)) {
      const promise = this.fetchThumbnail(item).finally(() => {
        this.inflight.delete(item.fileHash);
      });
      this.inflight.set(item.fileHash, promise);
    }

    return () => {
      const s = this.subscribers.get(item.fileHash);
      if (s === undefined) return;
      s.delete(callback);
      if (s.size === 0) {
        this.subscribers.delete(item.fileHash);
        // NU abort in-flight — promise rezolvă pentru viitoare subscribes (cache hit).
      }
    };
  }

  private isNegative(fileHash: string): boolean {
    const exp = this.negativeCache.get(fileHash);
    if (exp === undefined) return false;
    if (Date.now() > exp) {
      this.negativeCache.delete(fileHash);
      return false;
    }
    return true;
  }

  private async fetchThumbnail(item: QueueItem): Promise<void> {
    // Full WS direct — ZERO server cache.
    try {
      const result = await wsClient.requestThumbnail(
        item.agentId,
        item.fileHash,
        item.absPath,
        item.mtime,
        item.extKind,
      );
      if (result.pngBase64.length === 0) {
        const ttl = item.extKind === 'video' ? NEGATIVE_CACHE_MS_VIDEO : NEGATIVE_CACHE_MS_PICTURE;
        this.markNegativeAndNotify(item.fileHash, ttl);
        return;
      }
      const dataUri = `data:${result.mimeType};base64,${result.pngBase64}`;
      this.cachePositiveAndNotify(item.fileHash, dataUri);
    } catch {
      // WS error: timeout / agent offline / extraction failed.
      const ttl = item.extKind === 'video' ? NEGATIVE_CACHE_MS_VIDEO : NEGATIVE_CACHE_MS_PICTURE;
      this.markNegativeAndNotify(item.fileHash, ttl);
    }
  }

  private cachePositiveAndNotify(fileHash: string, url: string): void {
    this.positiveCache.set(fileHash, url);
    // LRU evict când depășește cap. Map insertion order = oldest first la iteration.
    while (this.positiveCache.size > POSITIVE_CACHE_MAX) {
      const oldestKey = this.positiveCache.keys().next().value;
      if (oldestKey === undefined) break;
      this.positiveCache.delete(oldestKey);
      // NU revoke — folosim URL string (REST endpoint) sau data URI (no GC concern).
    }
    const subs = this.subscribers.get(fileHash);
    if (subs !== undefined) {
      for (const s of subs) s(url);
      this.subscribers.delete(fileHash);
    }
  }

  private markNegativeAndNotify(fileHash: string, ttl: number): void {
    this.negativeCache.set(fileHash, Date.now() + ttl);
    this.persistNegativeCache();
    const subs = this.subscribers.get(fileHash);
    if (subs !== undefined) {
      for (const s of subs) s(null);
      this.subscribers.delete(fileHash);
    }
  }
}

// Singleton attached la window — previne multiple instances la HMR.
declare global { interface Window { __argusThumbQueue?: ThumbnailQueueImpl } }
const queue: ThumbnailQueueImpl = (() => {
  if (typeof window === 'undefined') return new ThumbnailQueueImpl();
  if (window.__argusThumbQueue !== undefined) return window.__argusThumbQueue;
  const q = new ThumbnailQueueImpl();
  window.__argusThumbQueue = q;
  return q;
})();

export const ThumbnailQueue = {
  subscribe: (item: QueueItem, callback: Subscriber): (() => void) => queue.subscribe(item, callback),
};

export async function computeFileHash(absPath: string, mtime: number): Promise<string> {
  const data = `${absPath.toLowerCase()}|${Math.floor(mtime)}`;
  const buf = new TextEncoder().encode(data);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function thumbnailUrl(agentId: string, fileHash: string): string {
  return `/api/v1/agents/${agentId}/thumbnails/file/${fileHash}`;
}

const PICTURE_EXTS = new Set(['jpg', 'jpeg', 'png', 'bmp', 'gif', 'webp', 'tiff']);
const VIDEO_EXTS = new Set(['mp4', 'webm', 'avi', 'mkv', 'mov', 'wmv']);

export function fileThumbKindFromName(name: string): FileThumbKind | null {
  const idx = name.lastIndexOf('.');
  if (idx < 0) return null;
  const ext = name.substring(idx + 1).toLowerCase();
  if (PICTURE_EXTS.has(ext)) return 'picture';
  if (VIDEO_EXTS.has(ext)) return 'video';
  return null;
}

